import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useEffect, useState } from "react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/industries", label: "Industries" },
  { to: "/products", label: "Products" },
  { to: "/markets", label: "Markets" },
  { to: "/blog", label: "Blog" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/92 border-b border-[#E3E6EC]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="logo-link"
        >
          <div className="w-10 h-10 bg-[#0A1F3F] text-white flex items-center justify-center font-display font-black text-sm border-b-4 border-[#F37021]">
            B
          </div>
          <div className="leading-none">
            <div className={`font-display font-black tracking-tight text-base ${scrolled ? "text-[#0B0D12]" : "text-white"}`}>
              BNM GROUP
            </div>
            <div className={`text-[10px] uppercase tracking-[0.22em] mt-0.5 ${scrolled ? "text-[#4B5668]" : "text-white/70"}`}>
              International Trading
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  scrolled
                    ? isActive
                      ? "text-[#0A1F3F]"
                      : "text-[#4B5668] hover:text-[#0A1F3F]"
                    : isActive
                    ? "text-white"
                    : "text-white/80 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/quote"
            data-testid="nav-cta-quote"
            className="hidden md:inline-flex items-center gap-2 bg-[#F37021] hover:bg-[#D35F16] text-white px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold transition-colors"
          >
            Request a Quote
            <ArrowUpRight className="w-4 h-4" />
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className={`lg:hidden w-11 h-11 flex items-center justify-center border ${
              scrolled ? "border-[#0A1F3F] text-[#0A1F3F]" : "border-white text-white"
            }`}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden bg-white border-t border-[#E3E6EC]"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {[...links, { to: "/contact", label: "Contact" }].map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-lg font-display font-bold ${
                    isActive ? "text-[#F37021]" : "text-[#0A1F3F]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/quote"
              data-testid="mobile-nav-quote"
              className="mt-3 inline-flex items-center justify-center gap-2 bg-[#F37021] text-white px-5 py-4 text-xs uppercase tracking-[0.18em] font-semibold"
            >
              Request a Quote
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
